import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Sessao } from '../../auth';
import {
  iniciais,
  listarEquipe,
  podeFazer,
  quando,
  reativarConta,
  suspenderConta,
  editarPessoa,
  trocarFuncao,
} from '../../acesso';
import type { Eu, Falha, FuncaoDaEquipe, MembroDaEquipe } from '../../acesso';
import { SemAcesso } from '../../design/Estados';
import { FotoDaConta } from '../../design/FotoDePerfil';
import { Icone } from '../../design/Icone';
import { CadastrarPessoa, ExcluirPessoa, SenhaProvisoria } from './EquipeAcoes';
import { UNIDADE } from './unidade';
import './acesso.css';

/**
 * TELA 31 — EQUIPE E USUÁRIOS. "Pessoa, conta, função e permissão são quatro coisas."
 *
 * Pessoa, código, função, unidade, acesso, último acesso e status. SEM meta, venda ou premiação:
 * cada coisa no seu módulo. Suspender não apaga nada — vendas, contagens e auditorias continuam
 * com a pessoa como autora; o servidor só troca o status e derruba as sessões abertas.
 *
 * O que o design desenha e o servidor ainda não tem aparece dito, nunca fingido: trocar função
 * (edição de perfis), redefinição de senha por WhatsApp e lista de sessões de outra pessoa.
 */

type Filtro = 'todos' | 'ativos' | 'pendentes' | 'suspensos' | 'nao_habilitado' | 'excluidos';
type Estado =
  | { readonly fase: 'carregando' }
  | { readonly fase: 'erro'; readonly falha: Falha }
  | { readonly fase: 'pronto'; readonly pessoas: readonly MembroDaEquipe[] };

const FUNCOES = ['Administrador', 'Balconista', 'Auditor'] as const;

export function Equipe({ sessao, eu }: { sessao: Sessao; eu: Eu | null }) {
  const [estado, setEstado] = useState<Estado>({ fase: 'carregando' });
  const [filtro, setFiltro] = useState<Filtro>('todos');
  const [funcao, setFuncao] = useState('');
  const [busca, setBusca] = useState('');
  const [aberto, setAberto] = useState<MembroDaEquipe | null>(null);
  const [adicionar, setAdicionar] = useState(false);
  const [cadastrar, setCadastrar] = useState(false);

  const carregar = useCallback(async () => {
    setEstado({ fase: 'carregando' });
    const r = await listarEquipe();
    setEstado(
      r.ok ? { fase: 'pronto', pessoas: r.dados.pessoas } : { fase: 'erro', falha: r.falha },
    );
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const pessoas = estado.fase === 'pronto' ? estado.pessoas : [];
  const conta = useMemo(() => contar(pessoas), [pessoas]);
  const visiveis = useMemo(() => {
    const termo = normalizar(busca);
    return pessoas.filter(
      (p) =>
        passaNoFiltro(p, filtro) &&
        (!funcao || funcaoDe(p) === funcao) &&
        (!termo ||
          normalizar(`${p.nome} ${p.login ?? ''} ${funcaoDe(p)} ${UNIDADE}`).includes(termo)),
    );
  }, [pessoas, filtro, funcao, busca]);

  if (estado.fase === 'erro' && estado.falha.tipo === 'sem-acesso') {
    return <SemAcesso area="Equipe" />;
  }

  const euLogin = eu?.login ?? null;
  const gerencia = podeFazer(eu, 'equipe.gerenciar');
  const aprova = podeFazer(eu, 'equipe.aprovar');

  return (
    <div className="ac ac-cheia">
      <div className="ac-cabeca">
        <div>
          <h1 className="ac-titulo">Equipe</h1>
          <div className="ac-subtitulo">Usuários, funções e acessos ao InovaPay · {UNIDADE}</div>
        </div>
        <div className="ac-acoes">
          <label className="ac-busca">
            <Icone nome="search" tamanho={14} traco={1.6} />
            <input
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Nome, código, função ou unidade"
              aria-label="Buscar na equipe"
            />
          </label>
          {gerencia && (
            <button
              type="button"
              className="ac-botao ac-botao-primario"
              onClick={() => setCadastrar(true)}
            >
              <Icone nome="plus" tamanho={14} traco={1.8} />
              Cadastrar pessoa
            </button>
          )}
          {aprova && (
            <button type="button" className="ac-botao" onClick={() => setAdicionar(true)}>
              <Icone nome="plus" tamanho={14} traco={1.8} />
              Adicionar pessoa
            </button>
          )}
        </div>
      </div>

      <div className="ac-kpis">
        <Kpi rotulo="ATIVOS" valor={conta.ativos} />
        <Kpi
          rotulo="AGUARDANDO APROVAÇÃO"
          valor={conta.pendentes}
          tom="alerta"
          href={aprova && (conta.pendentes ?? 0) > 0 ? '#/aprovacoes' : undefined}
        />
        <Kpi rotulo="SUSPENSOS" valor={conta.suspensos} tom="aviso" />
        <Kpi rotulo="ADMINISTRADORES" valor={conta.administradores} />
        <Kpi rotulo="BALCONISTAS" valor={conta.balconistas} />
        <Kpi rotulo="AUDITORES" valor={conta.auditores} />
      </div>

      <div className="ac-filtros">
        <Chip ativo={filtro === 'todos'} aoClicar={() => setFiltro('todos')}>
          Todos {estado.fase === 'pronto' ? pessoas.length : ''}
        </Chip>
        <Chip ativo={filtro === 'ativos'} aoClicar={() => setFiltro('ativos')}>
          Ativos {conta.ativos ?? ''}
        </Chip>
        <Chip alerta ativo={filtro === 'pendentes'} aoClicar={() => setFiltro('pendentes')}>
          Pendentes {conta.pendentes ?? ''}
        </Chip>
        <Chip ativo={filtro === 'suspensos'} aoClicar={() => setFiltro('suspensos')}>
          Suspensos {conta.suspensos ?? ''}
        </Chip>
        <Chip ativo={filtro === 'nao_habilitado'} aoClicar={() => setFiltro('nao_habilitado')}>
          Acesso não habilitado {conta.naoHabilitado ?? ''}
        </Chip>
        <Chip ativo={filtro === 'excluidos'} aoClicar={() => setFiltro('excluidos')}>
          Excluídos {pessoas.filter((p) => p.situacao === 'inativo').length || ''}
        </Chip>
        <span className="ac-divisor" />
        <label className="ac-seletor">
          <span className="ac-seletor-rotulo">Função</span>
          <select value={funcao} onChange={(e) => setFuncao(e.target.value)} aria-label="Função">
            <option value="">Todas</option>
            {FUNCOES.map((f) => (
              <option key={f}>{f}</option>
            ))}
          </select>
          <Icone nome="chev-d" tamanho={12} traco={1.6} />
        </label>
        <span className="ac-espaco" />
        <span className="ac-nota">
          acesso não habilitado se sobrepõe a pendentes e suspensos · uma unidade ativa
        </span>
      </div>

      <div className="ac-cartao ac-cartao-tabela">
        {estado.fase === 'carregando' && <Esqueleto />}
        {estado.fase === 'erro' && (
          <div style={{ padding: 16 }}>
            <ErroDaLista aoTentar={() => void carregar()} />
          </div>
        )}
        {estado.fase === 'pronto' && visiveis.length === 0 && (
          <Vazio filtrado={pessoas.length > 0} />
        )}
        {estado.fase === 'pronto' && visiveis.length > 0 && (
          <div className="ac-tabela-rolo">
            <table className="ac-tabela">
              <thead>
                <tr>
                  <th>PESSOA</th>
                  <th>CÓDIGO</th>
                  <th>FUNÇÃO</th>
                  <th className="ac-col-larga">UNIDADE</th>
                  <th className="ac-col-larga">ACESSO AO SISTEMA</th>
                  <th>ÚLTIMO ACESSO</th>
                  <th>STATUS DO CADASTRO</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {visiveis.map((p) => (
                  <Linha
                    key={p.accountId ?? p.pedidoId ?? p.nome}
                    pessoa={p}
                    souEu={p.login !== null && p.login === euLogin && p.accountId !== null}
                    aoAbrir={() => setAberto(p)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="ac-rodape-tabela">
          <span>
            {estado.fase === 'pronto'
              ? `${visiveis.length === 0 ? 0 : 1}–${String(visiveis.length)} de ${String(pessoas.length)} ${pessoas.length === 1 ? 'pessoa' : 'pessoas'}`
              : ' '}
          </span>
          <span>
            "Nunca acessou" não é o mesmo que inativo · sem meta, venda ou premiação nesta tabela
          </span>
        </div>
      </div>

      {/* Celular (31-B, 390 × 844): só o essencial — nome, função, código, status e último acesso. */}
      <div className="ac-cards">
        {estado.fase === 'carregando' && (
          <div className="ac-cartao">
            <Esqueleto />
          </div>
        )}
        {estado.fase === 'erro' && <ErroDaLista aoTentar={() => void carregar()} />}
        {estado.fase === 'pronto' && visiveis.length === 0 && (
          <div className="ac-cartao">
            <Vazio filtrado={pessoas.length > 0} />
          </div>
        )}
        {visiveis.map((p) => (
          <CardDaPessoa
            key={p.accountId ?? p.pedidoId ?? p.nome}
            pessoa={p}
            aoAbrir={() => setAberto(p)}
          />
        ))}
        {estado.fase === 'pronto' && (
          <div className="ac-nota" style={{ lineHeight: 1.5 }}>
            No celular só o essencial: nome, função, código, status e último acesso. Dados pessoais
            completos ficam no desktop.
          </div>
        )}
      </div>

      {aberto && (
        <PerfilDoUsuario
          pessoa={aberto}
          sessao={sessao}
          souEu={aberto.login === euLogin}
          gerencia={gerencia}
          aoFechar={() => setAberto(null)}
          aoMudar={(nova) => {
            setAberto(nova);
            void carregar();
          }}
        />
      )}
      {adicionar && <ComoAdicionar aoFechar={() => setAdicionar(false)} />}
      {cadastrar && (
        <CadastrarPessoa
          sessao={sessao}
          aoCancelar={() => setCadastrar(false)}
          aoCadastrar={() => {
            setCadastrar(false);
            void carregar();
          }}
        />
      )}
    </div>
  );
}

/* ================================================================== */

function Linha({
  pessoa: p,
  souEu,
  aoAbrir,
}: {
  pessoa: MembroDaEquipe;
  souEu: boolean;
  aoAbrir: () => void;
}) {
  const pendente = p.situacao === 'aguardando_aprovacao';
  return (
    <tr>
      <td>
        <div className="ac-pessoa">
          <span className={`ac-avatar${p.situacao === 'ativo' ? '' : ' ac-avatar-apagado'}`}>
            <FotoDaConta accountId={p.accountId} nome={p.nome} />
          </span>
          <div>
            <div className="ac-pessoa-nome">{p.nome}</div>
            {(souEu || pendente) && (
              <div className="ac-pessoa-sub">{souEu ? 'você' : 'pessoa cadastrada'}</div>
            )}
          </div>
        </div>
      </td>
      <td className={`ac-mono${pendente ? ' ac-fraco' : ''}`}>{pendente ? '—' : p.login}</td>
      <td className={pendente ? 'ac-cinza' : undefined}>
        {pendente ? (
          <>
            Balconista <span className="ac-pessoa-sub">solicitada</span>
          </>
        ) : (
          funcaoDe(p)
        )}
      </td>
      <td className="ac-cinza ac-col-larga">{UNIDADE}</td>
      <td className={`ac-col-larga ${p.situacao === 'ativo' ? 'ac-cinza' : 'ac-alerta-txt'}`}>
        {acessoDe(p)}
      </td>
      <td className={p.ultimoAcesso ? 'ac-num' : 'ac-fraco'}>
        {p.ultimoAcesso ? quando(p.ultimoAcesso) : 'nunca acessou'}
      </td>
      <td>
        <Status situacao={p.situacao} ate={p.suspensoAte} />
      </td>
      <td className="ac-direita">
        {pendente ? (
          <a className="ac-link" href={`#/aprovacoes?pedido=${p.pedidoId ?? ''}`}>
            Revisar
          </a>
        ) : (
          <button type="button" className="ac-link" onClick={aoAbrir}>
            Abrir
          </button>
        )}
      </td>
    </tr>
  );
}

function CardDaPessoa({ pessoa: p, aoAbrir }: { pessoa: MembroDaEquipe; aoAbrir: () => void }) {
  if (p.situacao === 'aguardando_aprovacao') {
    return (
      <div className="ac-card ac-card-alerta">
        <div className="ac-card-linha">
          <span className="ac-avatar ac-avatar-apagado">{iniciais(p.nome)}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="ac-card-nome">{p.nome}</div>
            <div className="ac-card-sub">Aguardando aprovação</div>
          </div>
        </div>
        <a
          className="ac-botao-cheio"
          style={{ display: 'grid', placeItems: 'center', textDecoration: 'none' }}
          href={`#/aprovacoes?pedido=${p.pedidoId ?? ''}`}
        >
          Revisar cadastro
        </a>
      </div>
    );
  }
  const partes = [
    funcaoDe(p),
    p.login ? `cód ${p.login}` : null,
    p.ultimoAcesso ? quando(p.ultimoAcesso) : null,
  ];
  return (
    <button type="button" className="ac-card" onClick={aoAbrir}>
      <div className="ac-card-linha">
        <span className={`ac-avatar${p.situacao === 'ativo' ? '' : ' ac-avatar-apagado'}`}>
          <FotoDaConta accountId={p.accountId} nome={p.nome} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="ac-card-nome">{p.nome}</div>
          <div className="ac-card-sub">{partes.filter(Boolean).join(' · ')}</div>
        </div>
        <Status situacao={p.situacao} ate={p.suspensoAte} />
      </div>
    </button>
  );
}

export function Status({
  situacao,
  ate = null,
}: {
  situacao: MembroDaEquipe['situacao'];
  ate?: string | null;
}) {
  if (situacao === 'ativo')
    return (
      <span className="ac-status ac-status-ativo">
        <span className="ac-status-ponto" />
        Ativo
      </span>
    );
  if (situacao === 'suspenso')
    return (
      <span className="ac-status ac-status-suspenso">
        <Icone nome="lock" tamanho={11} traco={1.8} />
        {ate ? `Suspenso até ${ateCurto(ate)}` : 'Suspenso'}
      </span>
    );
  if (situacao === 'inativo') return <span className="ac-status ac-status-inativo">Inativo</span>;
  return <span className="ac-status ac-status-pendente">Aguardando aprovação</span>;
}

function Kpi({
  rotulo,
  valor,
  tom,
  href,
}: {
  rotulo: string;
  valor: number | null;
  tom?: 'alerta' | 'aviso';
  href?: string | undefined;
}) {
  const classe = `ac-kpi${tom === 'alerta' && valor ? ' ac-kpi-alerta' : ''}${tom === 'aviso' && valor ? ' ac-kpi-aviso' : ''}`;
  const conteudo = (
    <>
      <div className="ac-kpi-rotulo">{rotulo}</div>
      <div className="ac-kpi-valor">{valor ?? '—'}</div>
    </>
  );
  return href ? (
    <a className={classe} href={href} style={{ textDecoration: 'none' }}>
      {conteudo}
    </a>
  ) : (
    <div className={classe}>{conteudo}</div>
  );
}

function Chip({
  ativo,
  alerta,
  aoClicar,
  children,
}: {
  ativo: boolean;
  alerta?: boolean;
  aoClicar: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      className={`ac-chip${alerta ? ' ac-chip-alerta' : ''}${ativo ? ' ac-chip-ativo' : ''}`}
      aria-pressed={ativo}
      onClick={aoClicar}
    >
      {children}
    </button>
  );
}

export function Esqueleto({ linhas = 3 }: { linhas?: number }) {
  return (
    <div aria-busy="true" aria-label="Carregando">
      <div
        style={{
          height: 30,
          background: 'var(--ink-50)',
          borderBottom: '1px solid var(--ink-150)',
        }}
      />
      {Array.from({ length: linhas }, (_, i) => (
        <div className="ac-esqueleto" key={i}>
          <i />
          <i style={{ width: '28%' }} />
          <span className="ac-espaco" />
          <i style={{ width: '14%' }} />
        </div>
      ))}
    </div>
  );
}

function Vazio({ filtrado }: { filtrado: boolean }) {
  return (
    <div className="ac-vazio">
      <div className="ac-vazio-titulo">
        {filtrado ? 'Nenhum usuário corresponde aos filtros' : 'Ninguém na equipe ainda'}
      </div>
      <div className="ac-vazio-texto">
        {filtrado
          ? 'Tente outro filtro ou limpe a busca.'
          : 'Quem pedir acesso pela tela de entrada aparece aqui, aguardando aprovação.'}
      </div>
    </div>
  );
}

export function ErroDaLista({
  aoTentar,
  titulo = 'Não foi possível carregar a equipe',
  texto = 'Só esta área falhou. Tente novamente.',
}: {
  aoTentar: () => void;
  titulo?: string;
  texto?: string;
}) {
  return (
    <div className="ac-erro" role="alert">
      <div className="ac-erro-titulo">{titulo}</div>
      <div className="ac-erro-texto">{texto}</div>
      <button type="button" className="ac-botao" onClick={aoTentar}>
        <Icone nome="retry" tamanho={14} traco={1.7} />
        Tentar de novo
      </button>
    </div>
  );
}

/* ================================================================== *
 * Drawer — perfil do usuário (31-B)
 * ================================================================== */

type Aba = 'acesso' | 'seguranca' | 'historico';

function PerfilDoUsuario({
  pessoa: p,
  sessao,
  souEu,
  gerencia,
  aoFechar,
  aoMudar,
}: {
  pessoa: MembroDaEquipe;
  sessao: Sessao;
  souEu: boolean;
  gerencia: boolean;
  aoFechar: () => void;
  aoMudar: (nova: MembroDaEquipe) => void;
}) {
  const [aba, setAba] = useState<Aba>('acesso');
  const [suspender, setSuspender] = useState(false);
  const [provisoria, setProvisoria] = useState(false);
  const [excluir, setExcluir] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const [reativando, setReativando] = useState(false);
  const funcaoAtual = FUNCOES_DA_CONTA.find((f) => p.perfis.includes(f.nome))?.codigo ?? '';
  const [funcaoNova, setFuncaoNova] = useState<FuncaoDaEquipe | ''>(funcaoAtual);
  const [trocando, setTrocando] = useState(false);
  const [confirmarTroca, setConfirmarTroca] = useState(false);
  const [trocou, setTrocou] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const secoes = {
    acesso: useRef<HTMLDivElement>(null),
    seguranca: useRef<HTMLDivElement>(null),
    historico: useRef<HTMLDivElement>(null),
  };

  useEscape(aoFechar, !suspender && !confirmarTroca && !provisoria && !excluir);

  const ir = (a: Aba) => {
    setAba(a);
    secoes[a].current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  async function reativar() {
    if (!p.accountId || reativando) return;
    setReativando(true);
    setErro(null);
    const r = await reativarConta(sessao, p.accountId);
    setReativando(false);
    if (r.ok) aoMudar({ ...p, situacao: 'ativo' });
    else setErro(mensagemDeFalha(r.falha));
  }

  async function trocar() {
    if (!p.accountId || !funcaoNova || funcaoNova === funcaoAtual || trocando) return;
    setTrocando(true);
    setErro(null);
    setTrocou(null);
    const r = await trocarFuncao(sessao, p.accountId, funcaoNova);
    setTrocando(false);
    setConfirmarTroca(false);
    if (!r.ok) {
      setErro(mensagemDeFalha(r.falha));
      return;
    }
    const nome = FUNCOES_DA_CONTA.find((f) => f.codigo === funcaoNova)?.nome ?? funcaoNova;
    // os perfis de dono (se houver) ficam; só a função do dia a dia muda
    const outros = p.perfis.filter((x) => !FUNCOES_DA_CONTA.some((f) => f.nome === x));
    aoMudar({ ...p, perfis: [nome, ...outros] });
    setTrocou(`Agora é ${nome}. As sessões abertas já valem com a função nova.`);
  }

  const nomeCurto = p.nome.split(/\s+/).slice(0, 2).join(' ');

  return (
    <div className="ac-veu" onClick={aoFechar} role="presentation">
      <aside
        className="ac-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={`Perfil de ${p.nome}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ac-drawer-cabeca">
          <span className={`ac-avatar${p.situacao === 'ativo' ? '' : ' ac-avatar-apagado'}`}>
            <FotoDaConta accountId={p.accountId} nome={p.nome} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="ac-drawer-nome">{p.nome}</div>
            <div className="ac-drawer-meta">
              {p.login && <span className="ac-mono">cód {p.login}</span>}
              <span>
                {funcaoDe(p)} · {UNIDADE}
              </span>
              <Status situacao={p.situacao} ate={p.suspensoAte} />
            </div>
          </div>
          <button type="button" className="ac-fechar" onClick={aoFechar} aria-label="Fechar">
            <Icone nome="x" tamanho={16} traco={1.8} />
          </button>
        </div>

        <div className="ac-abas" role="tablist">
          {(
            [
              ['acesso', 'Acesso'],
              ['seguranca', 'Segurança'],
              ['historico', 'Histórico'],
            ] as const
          ).map(([id, rotulo]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={aba === id}
              className={`ac-aba${aba === id ? ' ac-aba-ativa' : ''}`}
              onClick={() => ir(id)}
            >
              {rotulo}
            </button>
          ))}
        </div>

        <div className="ac-secao" ref={secoes.acesso}>
          <div className="ac-rotulo-mono ac-rotulo-mono-marca" style={{ marginBottom: 11 }}>
            FUNÇÃO E UNIDADE
          </div>
          <div className="ac-grade-2">
            <div>
              <div className="ac-campo-rotulo">Função no InovaPay</div>
              <div className="ac-caixa">{funcaoDe(p)}</div>
            </div>
            <div>
              <div className="ac-campo-rotulo">Perfil de acesso</div>
              <div className="ac-caixa">{p.perfis.join(' · ') || 'Sem perfil'}</div>
            </div>
          </div>
          <div className="ac-faixa">
            <Icone nome="pin" tamanho={16} traco={1.7} />
            <div style={{ flex: 1 }}>
              <div className="ac-faixa-titulo">Unidade principal · {UNIDADE}</div>
              <div className="ac-faixa-texto">
                Única unidade permitida — o seletor não aparece para ninguém
              </div>
            </div>
          </div>
          <div className="ac-aviso">
            <Icone nome="warn" tamanho={16} traco={1.7} />
            <div>
              <b>Trocar a função muda o acesso daqui para frente.</b> Vendas, contagens e auditorias
              já registradas continuam com {primeiro(p.nome)} como autora, na função do momento.
            </div>
          </div>
          {gerencia && !souEu && p.accountId && p.situacao !== 'aguardando_aprovacao' && (
            <div className="ac-trocar-funcao">
              <label>
                <span className="ac-campo-rotulo">Trocar a função</span>
                <select
                  value={funcaoNova}
                  onChange={(e) => setFuncaoNova(e.target.value as FuncaoDaEquipe)}
                  aria-label="Nova função"
                >
                  {funcaoAtual === '' && <option value="">Escolher…</option>}
                  {FUNCOES_DA_CONTA.map((f) => (
                    <option key={f.codigo} value={f.codigo}>
                      {f.nome}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                className="ac-botao ac-botao-primario"
                disabled={!funcaoNova || funcaoNova === funcaoAtual || trocando}
                onClick={() => setConfirmarTroca(true)}
              >
                {trocando ? 'Trocando…' : 'Trocar função'}
              </button>
              {trocou && <span className="ac-trocou">{trocou}</span>}
            </div>
          )}
        </div>

        {gerencia && !souEu && p.accountId && p.situacao !== 'aguardando_aprovacao' && (
          <DadosDaPessoa p={p} sessao={sessao} aoMudar={aoMudar} />
        )}

        <div className="ac-secao" ref={secoes.seguranca}>
          <div className="ac-secao-cabeca">
            <div className="ac-rotulo-mono ac-rotulo-mono-marca">SEGURANÇA</div>
            <span className="ac-espaco" />
            <span className="ac-nota" style={{ fontSize: 11 }}>
              nenhuma senha, hash ou token é exibido
            </span>
          </div>
          <div className="ac-info">
            <Icone nome="key" tamanho={15} traco={1.7} />
            <div>
              A senha é só da pessoa: ela troca em <b>Minha conta</b>, sabendo a atual. Esqueceu? Dê
              uma <b>senha provisória</b>: ela entra com a provisória e é obrigada a criar a dela —
              o administrador nunca fica sabendo a senha definitiva.
            </div>
          </div>
          {gerencia && !souEu && p.accountId && p.situacao === 'ativo' && (
            <button
              type="button"
              className="ac-botao"
              style={{ marginTop: 10 }}
              onClick={() => setProvisoria(true)}
            >
              <Icone nome="key" tamanho={14} traco={1.7} /> Dar senha provisória
            </button>
          )}
          {aviso && (
            <p className="ac-nota" role="status" style={{ marginTop: 8 }}>
              {aviso}
            </p>
          )}
          <div className="ac-pe-modal">
            Suspender encerra na hora todas as sessões abertas desta pessoa — é o servidor quem
            executa.
          </div>
        </div>

        <div className="ac-secao" ref={secoes.historico}>
          <div className="ac-rotulo-mono">HISTÓRICO DE ACESSO</div>
          <div className="ac-historico">
            <span className="ac-historico-hora">
              {p.ultimoAcesso ? quando(p.ultimoAcesso) : '—'}
            </span>
            <div style={{ flex: 1 }}>
              {p.ultimoAcesso ? 'Último acesso ao InovaPay' : 'Ainda não acessou o InovaPay'}
            </div>
          </div>
          <div className="ac-pe-modal">
            Cada mudança de acesso fica na trilha de auditoria, com autor e hora (Central de
            Segurança).
          </div>
        </div>

        {erro && (
          <div style={{ padding: '14px 18px 0' }}>
            <ErroDaLista
              titulo="Não foi possível atualizar o acesso"
              texto={`${erro} Nada foi alterado.`}
              aoTentar={() => void reativar()}
            />
          </div>
        )}

        <div className="ac-rodape-drawer">
          <span className="ac-cinza" style={{ fontSize: 12, marginRight: 4 }}>
            Ver em outros módulos:
          </span>
          <a href="#/vendas">Vendas</a>
          <span className="ac-sep">·</span>
          <a href="#/premiacao">Premiação</a>
          <span className="ac-sep">·</span>
          <a href="#/giro">Giro e rodízio</a>
          <span className="ac-sep">·</span>
          <a href="#/auditoria">Auditorias</a>
          <span className="ac-espaco" />
          {gerencia && !souEu && p.accountId && p.situacao === 'ativo' && (
            <button
              type="button"
              className="ac-botao ac-botao-g ac-botao-perigo"
              onClick={() => setSuspender(true)}
            >
              Suspender acesso
            </button>
          )}
          {gerencia &&
            !souEu &&
            p.accountId &&
            p.situacao !== 'inativo' &&
            p.situacao !== 'aguardando_aprovacao' && (
              <button
                type="button"
                className="ac-botao ac-botao-g ac-botao-perigo"
                onClick={() => setExcluir(true)}
              >
                Excluir
              </button>
            )}
          {gerencia &&
            !souEu &&
            p.accountId &&
            (p.situacao === 'suspenso' || p.situacao === 'inativo') && (
              <button
                type="button"
                className="ac-botao ac-botao-g"
                disabled={reativando}
                onClick={() => void reativar()}
              >
                {reativando ? 'Reativando…' : 'Reativar acesso'}
              </button>
            )}
          {souEu && <span className="ac-nota">Você não pode suspender a própria conta.</span>}
        </div>
      </aside>

      {provisoria && p.accountId && (
        <SenhaProvisoria
          sessao={sessao}
          accountId={p.accountId}
          nome={p.nome}
          aoCancelar={() => setProvisoria(false)}
          aoFeito={() => {
            setProvisoria(false);
            setAviso('Senha provisória salva. No próximo acesso a pessoa cria a senha dela.');
          }}
        />
      )}
      {excluir && p.accountId && (
        <ExcluirPessoa
          sessao={sessao}
          accountId={p.accountId}
          nome={p.nome}
          aoCancelar={() => setExcluir(false)}
          aoFeito={() => {
            setExcluir(false);
            aoMudar({ ...p, situacao: 'inativo' });
          }}
        />
      )}
      {confirmarTroca && p.accountId && funcaoNova && (
        <ConfirmarTrocaDeFuncao
          nome={nomeCurto}
          de={funcaoDe(p)}
          para={FUNCOES_DA_CONTA.find((f) => f.codigo === funcaoNova)?.nome ?? funcaoNova}
          enviando={trocando}
          aoCancelar={() => setConfirmarTroca(false)}
          aoConfirmar={() => void trocar()}
        />
      )}

      {suspender && p.accountId && (
        <ConfirmarSuspensao
          nome={nomeCurto}
          detalhe={`${funcaoDe(p)}${p.login ? ` · cód ${p.login}` : ''} · ${UNIDADE}`}
          aoCancelar={() => setSuspender(false)}
          aoConfirmar={async (motivo, dias) => {
            const r = await suspenderConta(sessao, p.accountId ?? '', motivo, dias);
            if (r.ok) {
              setSuspender(false);
              aoMudar({ ...p, situacao: 'suspenso', suspensoAte: dias ? fimDoPrazo(dias) : null });
              return null;
            }
            return mensagemDeFalha(r.falha);
          }}
        />
      )}
    </div>
  );
}

/**
 * Trocar a função muda o acesso na hora (as sessões abertas já valem com a função nova): por isso
 * pede confirmação com o DE → PARA à vista. O servidor ainda não guarda motivo para a troca — não
 * pedimos um campo que não seria gravado.
 */
function ConfirmarTrocaDeFuncao({
  nome,
  de,
  para,
  enviando,
  aoCancelar,
  aoConfirmar,
}: {
  nome: string;
  de: string;
  para: string;
  enviando: boolean;
  aoCancelar: () => void;
  aoConfirmar: () => void;
}) {
  useEscape(aoCancelar, !enviando);
  return (
    <div
      className="ac-veu ac-veu-centro"
      onClick={(e) => {
        e.stopPropagation();
        if (!enviando) aoCancelar();
      }}
      role="presentation"
    >
      <div
        className="ac-modal"
        role="alertdialog"
        aria-modal="true"
        aria-label={`Trocar a função de ${nome}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ac-modal-cabeca">
          <div className="ac-modal-titulo">Trocar a função de {nome}?</div>
          <div className="ac-modal-sub">
            {de} → <b>{para}</b>
          </div>
        </div>
        <div className="ac-modal-corpo">
          <div className="ac-efeitos">
            <div className="ac-efeitos-titulo">O QUE ACONTECE</div>
            <div className="ac-efeito ac-efeito-nao">
              <Icone nome="x" tamanho={14} traco={1.9} />
              <span>O acesso muda agora, inclusive nas sessões já abertas</span>
            </div>
            <div className="ac-efeito ac-efeito-sim">
              <Icone nome="check" tamanho={14} traco={2.2} />
              <span>
                Vendas, contagens e auditorias já registradas <b>permanecem</b> com a pessoa como
                autora
              </span>
            </div>
          </div>
          <div className="ac-botoes-modal">
            <button
              type="button"
              className="ac-botao-cheio"
              disabled={enviando}
              onClick={aoConfirmar}
              autoFocus
            >
              {enviando ? 'Trocando…' : `Trocar para ${para}`}
            </button>
            <button
              type="button"
              className="ac-botao-contorno"
              onClick={aoCancelar}
              disabled={enviando}
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ConfirmarSuspensao({
  nome,
  detalhe,
  aoCancelar,
  aoConfirmar,
}: {
  nome: string;
  detalhe: string;
  aoCancelar: () => void;
  aoConfirmar: (motivo: string, dias: number | null) => Promise<string | null>;
}) {
  const [motivo, setMotivo] = useState('');
  const [dias, setDias] = useState<number | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  useEscape(aoCancelar, !enviando);

  async function confirmar() {
    if (enviando || !motivo.trim()) return;
    setEnviando(true);
    setErro(null);
    const falha = await aoConfirmar(motivo.trim(), dias);
    setEnviando(false);
    if (falha) setErro(falha);
  }

  return (
    <div
      className="ac-veu ac-veu-centro"
      onClick={(e) => {
        e.stopPropagation();
        if (!enviando) aoCancelar();
      }}
      role="presentation"
    >
      <div
        className="ac-modal ac-modal-perigo"
        role="alertdialog"
        aria-modal="true"
        aria-label={`Suspender acesso de ${nome}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ac-modal-cabeca">
          <div className="ac-modal-titulo">Suspender acesso de {nome}</div>
          <div className="ac-modal-sub">{detalhe}</div>
        </div>
        <div className="ac-modal-corpo">
          <div className="ac-efeitos">
            <div className="ac-efeitos-titulo">O QUE ACONTECE</div>
            <div className="ac-efeito ac-efeito-nao">
              <Icone nome="x" tamanho={14} traco={1.9} />
              <span>O acesso ao InovaPay é bloqueado</span>
            </div>
            <div className="ac-efeito ac-efeito-nao">
              <Icone nome="x" tamanho={14} traco={1.9} />
              <span>
                Efeitos nos módulos operacionais (como o Giro) seguem as regras do sistema
              </span>
            </div>
            <div className="ac-efeito ac-efeito-sim">
              <Icone nome="check" tamanho={14} traco={2.2} />
              <span>
                Vendas, contagens, auditorias e premiações <b>permanecem</b> com a pessoa como
                autora
              </span>
            </div>
            <div className="ac-efeito ac-efeito-sim">
              <Icone nome="check" tamanho={14} traco={2.2} />
              <span>O cadastro continua — suspender não é excluir</span>
            </div>
          </div>
          <div className="ac-efeitos-titulo">MOTIVO</div>
          <textarea
            className="ac-texto-area"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Ex.: afastamento temporário"
            maxLength={500}
            autoFocus
          />
          <div className="ac-efeitos-titulo">POR QUANTO TEMPO</div>
          <div className="ac-prazo" role="radiogroup" aria-label="Prazo da suspensão">
            {PRAZOS.map((d) => (
              <button
                key={String(d)}
                type="button"
                role="radio"
                aria-checked={dias === d}
                className={`ac-chip${dias === d ? ' ac-chip-ativo' : ''}`}
                onClick={() => setDias(d)}
              >
                {d === null ? 'Até eu reativar' : d === 1 ? '1 dia' : `${String(d)} dias`}
              </button>
            ))}
          </div>
          <p className="ac-prazo-nota">
            {dias === null
              ? 'Fica suspensa até alguém reativar na Equipe.'
              : `Volta sozinha em ${ateCurto(fimDoPrazo(dias))}, à 0h — o servidor libera e registra na trilha.`}
          </p>
          <div className="ac-info" style={{ marginBottom: 12 }}>
            <span className="ac-caixinha ac-caixinha-marcada" aria-hidden="true">
              <Icone nome="check" tamanho={12} traco={2.6} />
            </span>
            <div>
              Encerrar sessões abertas agora{' '}
              <span className="ac-info-fraco">— o servidor faz isso sempre, na hora</span>
            </div>
          </div>
          {erro && (
            <div className="ac-erro" role="alert" style={{ marginBottom: 12 }}>
              <div className="ac-erro-titulo">Não foi possível atualizar o acesso</div>
              <div className="ac-erro-texto">{erro} Nada foi alterado.</div>
            </div>
          )}
          <div className="ac-botoes-modal">
            <button
              type="button"
              className="ac-botao-cheio"
              disabled={enviando || !motivo.trim()}
              onClick={() => void confirmar()}
            >
              {enviando ? 'Suspendendo…' : 'Confirmar suspensão'}
            </button>
            <button
              type="button"
              className="ac-botao-contorno"
              onClick={aoCancelar}
              disabled={enviando}
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ComoAdicionar({ aoFechar }: { aoFechar: () => void }) {
  useEscape(aoFechar, true);
  return (
    <div className="ac-veu ac-veu-centro" onClick={aoFechar} role="presentation">
      <div
        className="ac-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Adicionar pessoa"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ac-modal-cabeca">
          <div className="ac-modal-titulo">Adicionar pessoa</div>
          <div className="ac-modal-sub">
            A pessoa pede; você decide. Ninguém cria senha por ela.
          </div>
        </div>
        <div className="ac-modal-corpo">
          <div className="ac-efeitos">
            <div className="ac-efeito">
              <b style={{ width: 16 }}>1</b>
              <span>
                No celular dela, abrir o InovaPay e tocar em <b>Solicitar acesso</b>.
              </span>
            </div>
            <div className="ac-efeito">
              <b style={{ width: 16 }}>2</b>
              <span>Ela preenche nome, WhatsApp e cria a própria senha.</span>
            </div>
            <div className="ac-efeito">
              <b style={{ width: 16 }}>3</b>
              <span>
                O pedido aparece em <b>Aprovações</b>: você confere, define o código e aprova.
              </span>
            </div>
          </div>
          <div className="ac-botoes-modal">
            <a
              className="ac-botao-cheio"
              style={{ display: 'grid', placeItems: 'center', textDecoration: 'none' }}
              href="#/aprovacoes"
            >
              Ir para Aprovações
            </a>
            <button type="button" className="ac-botao-contorno" onClick={aoFechar}>
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ================================================================== */

/** As funções do dia a dia (o nome é o do perfil no banco). Dono não é função: só pelo terminal. */
const FUNCOES_DA_CONTA: readonly { codigo: FuncaoDaEquipe; nome: string }[] = [
  { codigo: 'balconista', nome: 'Balconista' },
  { codigo: 'admin_geral', nome: 'Administrador' },
  { codigo: 'auditor', nome: 'Auditor' },
  { codigo: 'receptor', nome: 'Receptor' },
];

function funcaoDe(p: MembroDaEquipe): string {
  if (p.situacao === 'aguardando_aprovacao') return 'Balconista';
  return p.perfis[0] ?? 'Sem perfil';
}

function acessoDe(p: MembroDaEquipe): string {
  if (p.situacao === 'ativo') return 'Habilitado';
  if (p.situacao === 'aguardando_aprovacao') return 'Ainda não habilitado';
  return 'Não habilitado';
}

function passaNoFiltro(p: MembroDaEquipe, f: Filtro): boolean {
  // Excluído (conta inativa) só aparece no filtro dele (dono, 29/09: "desativar e esconder").
  if (f === 'excluidos') return p.situacao === 'inativo';
  if (p.situacao === 'inativo') return false;
  if (f === 'ativos') return p.situacao === 'ativo';
  if (f === 'pendentes') return p.situacao === 'aguardando_aprovacao';
  if (f === 'suspensos') return p.situacao === 'suspenso';
  if (f === 'nao_habilitado') return p.situacao !== 'ativo';
  return true;
}

function contar(pessoas: readonly MembroDaEquipe[]) {
  if (pessoas.length === 0)
    return {
      ativos: null,
      pendentes: null,
      suspensos: null,
      naoHabilitado: null,
      administradores: null,
      balconistas: null,
      auditores: null,
    } as const;
  const ativos = pessoas.filter((p) => p.situacao === 'ativo');
  const comFuncao = (f: string) => ativos.filter((p) => p.perfis.includes(f)).length;
  return {
    ativos: ativos.length,
    pendentes: pessoas.filter((p) => p.situacao === 'aguardando_aprovacao').length,
    suspensos: pessoas.filter((p) => p.situacao === 'suspenso').length,
    naoHabilitado: pessoas.filter((p) => p.situacao !== 'ativo').length,
    administradores: comFuncao('Administrador'),
    balconistas: comFuncao('Balconista'),
    auditores: comFuncao('Auditor'),
  };
}

function normalizar(t: string): string {
  return t.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}

function primeiro(nome: string): string {
  return nome.split(/\s+/)[0] ?? nome;
}

export function mensagemDeFalha(falha: Falha): string {
  switch (falha.tipo) {
    case 'conflito':
    case 'invalido':
      return falha.mensagem || 'O servidor recusou a operação.';
    case 'sem-acesso':
      return 'Você não tem permissão para esta ação.';
    case 'sem-sessao':
      return 'Sua sessão terminou. Entre de novo.';
    case 'offline':
      return 'Sem conexão com o InovaPay.';
    case 'limite':
      return 'Muitas tentativas seguidas. Aguarde alguns minutos.';
    default:
      return `Erro inesperado${falha.codigo ? ` (código ${falha.codigo})` : ''}.`;
  }
}

export function useEscape(aoFechar: () => void, ativo: boolean) {
  useEffect(() => {
    if (!ativo) return;
    const tecla = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        aoFechar();
      }
    };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, [aoFechar, ativo]);
}

/** Prazos da suspensão (a "falta" do 1.0). `null` = até alguém reativar. */
const PRAZOS: readonly (number | null)[] = [null, 1, 2, 3, 7, 15];

/** O fim do prazo como o servidor calcula: começo do dia (hoje + N) em Brasília. */
function fimDoPrazo(dias: number): string {
  const hoje = new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });
  const d = new Date(`${hoje}T03:00:00Z`);
  d.setUTCDate(d.getUTCDate() + dias);
  return d.toISOString();
}
const ateCurto = (iso: string) =>
  new Date(iso).toLocaleDateString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
  });

/** Nome e código de acesso (o 1.0 deixava o admin editar). A senha nunca é campo aqui. */
function DadosDaPessoa({
  p,
  sessao,
  aoMudar,
}: {
  p: MembroDaEquipe;
  sessao: Sessao;
  aoMudar: (nova: MembroDaEquipe) => void;
}) {
  const [editando, setEditando] = useState(false);
  const [nome, setNome] = useState(p.nome);
  const [exibicao, setExibicao] = useState('');
  const [login, setLogin] = useState(p.login ?? '');
  const [enviando, setEnviando] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; texto: string } | null>(null);
  const mudou =
    nome.trim() !== p.nome || login.trim() !== (p.login ?? '') || exibicao.trim() !== '';
  async function salvar() {
    if (!p.accountId || !mudou || enviando) return;
    setEnviando(true);
    setMsg(null);
    const r = await editarPessoa(sessao, p.accountId, {
      ...(nome.trim() !== p.nome ? { nome: nome.trim() } : {}),
      ...(exibicao.trim() ? { nomeExibicao: exibicao.trim() } : {}),
      ...(login.trim() !== (p.login ?? '') ? { login: login.trim() } : {}),
    });
    setEnviando(false);
    if (!r.ok) return setMsg({ ok: false, texto: mensagemDeFalha(r.falha) });
    aoMudar({ ...p, nome: r.dados.nome, login: r.dados.login });
    setEditando(false);
    setExibicao('');
    setMsg({
      ok: true,
      texto:
        r.dados.login !== (p.login ?? '')
          ? `Salvo. Daqui para frente ${primeiro(r.dados.nome)} entra com o código ${r.dados.login}.`
          : 'Salvo.',
    });
  }
  return (
    <div className="ac-secao">
      <div className="ac-secao-cabeca">
        <div className="ac-rotulo-mono ac-rotulo-mono-marca">DADOS DA PESSOA</div>
        <span className="ac-espaco" />
        {!editando && (
          <button type="button" className="ac-botao" onClick={() => setEditando(true)}>
            Editar
          </button>
        )}
      </div>
      {editando ? (
        <div className="ac-dados-form">
          <label>
            <span className="ac-campo-rotulo">Nome completo</span>
            <input value={nome} maxLength={120} onChange={(e) => setNome(e.target.value)} />
          </label>
          <label>
            <span className="ac-campo-rotulo">Nome na tela (opcional)</span>
            <input
              value={exibicao}
              maxLength={60}
              placeholder="Ex.: Ana Paula"
              onChange={(e) => setExibicao(e.target.value)}
            />
          </label>
          <label>
            <span className="ac-campo-rotulo">Código de acesso</span>
            <input
              className="ac-mono"
              value={login}
              maxLength={40}
              onChange={(e) => setLogin(e.target.value)}
            />
          </label>
          <p className="ac-nota">
            A senha não muda aqui: a pessoa troca em Minha conta; esqueceu, é no computador da loja.
          </p>
          <div className="ac-dados-botoes">
            <button
              type="button"
              className="ac-botao ac-botao-primario"
              disabled={!mudou || enviando || nome.trim().length < 2 || !login.trim()}
              onClick={() => void salvar()}
            >
              {enviando ? 'Salvando…' : 'Salvar'}
            </button>
            <button
              type="button"
              className="ac-botao"
              disabled={enviando}
              onClick={() => {
                setEditando(false);
                setNome(p.nome);
                setLogin(p.login ?? '');
                setExibicao('');
                setMsg(null);
              }}
            >
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        <div className="ac-grade-2">
          <div>
            <div className="ac-campo-rotulo">Nome completo</div>
            <div className="ac-caixa">{p.nome}</div>
          </div>
          <div>
            <div className="ac-campo-rotulo">Código de acesso</div>
            <div className="ac-caixa ac-mono">{p.login ?? '—'}</div>
          </div>
        </div>
      )}
      {msg && (
        <p className={msg.ok ? 'ac-trocou' : 'ac-erro-texto'} role="status">
          {msg.texto}
        </p>
      )}
    </div>
  );
}
